import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import { getEntryChanges, previewEntryChanges, saveEntryChanges } from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";
import { fmtDay, partName, rupees, todayIso } from "../rates/rateModel";
import { newRequestId } from "../rounds/entryQueue";
import { askReason } from "../ui/dialog";
import { cleanSareeNumber } from "./sareeEditModel";
import {
  ACTION_LABEL,
  batchRequest,
  canConfirm,
  ChangeRequest,
  changeFromForm,
  changeRequest,
  confirmRequest,
  EntryChangeLog,
  EntryForm,
  formFromEntry,
  formProblem,
  HistoryEntry,
  keepRequestId,
  needsFreshPreview,
  needsPaidRecheck,
  PreviewView,
  previewView,
  problemText,
  snapshotLine,
  refusalView,
} from "./entryChangeModel";

export type ChangeTarget =
  | { kind: "edit"; entry: HistoryEntry }
  | { kind: "remove"; entries: HistoryEntry[]; reason: string }
  | { kind: "restore"; entry: HistoryEntry; reason: string };

type Props = {
  target: ChangeTarget | null;
  sareeNumber: string;
  isOwner: boolean;
  roster: { id: string; code: string; name?: string }[];
  types: any[];
  onClose: () => void;
  /** Something was saved: reload the history. */
  onDone: () => void;
};

const NO_CONNECTION = "No connection. It may not have saved; try again.";

/** A remove or restore is previewed as soon as the dialog opens: one batch, one reason. */
const batchFor = (target: Exclude<ChangeTarget, { kind: "edit" }>): ChangeRequest =>
  target.kind === "remove"
    ? batchRequest(
        "REMOVE",
        target.entries.map((e) => e.id),
        target.reason,
      )
    : batchRequest("RESTORE", [target.entry.id], target.reason);

/**
 * Owner/manager change of saved entries: edit (also another day or saree), remove one or several,
 * or restore. Always shows the server's preview (before → after, pay, approvals, a paid entry's
 * adjust choice) and confirms with that preview's versions. Saving never stays stuck: every call
 * clears busy in finally and shows the server's message.
 */
const EntryChangeDialog = ({ target, sareeNumber, isOwner, roster, types, onClose, onDone }: Props) => {
  const { t, lang } = useKT();
  const entry = target && target.kind !== "remove" ? target.entry : null;
  const [was, setWas] = useState<EntryForm | null>(null);
  const [form, setForm] = useState<EntryForm | null>(null);
  const [req, setReq] = useState<ChangeRequest | null>(null);
  const [view, setView] = useState<PreviewView | null>(null);
  const [adjust, setAdjust] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One id per previewed change: a retry after a lost answer replays it instead of saving twice.
  const [requestId, setRequestId] = useState("");
  const [log, setLog] = useState<EntryChangeLog[]>([]);

  /** `note`: a message to keep showing over the new preview (why it was checked again). */
  const preview = async (r: ChangeRequest, note: string | null = null) => {
    setBusy(true);
    setError(note);
    try {
      const res: any = await previewEntryChanges(r as any);
      if (!res?.success) {
        setError(problemText(res));
        return;
      }
      setReq(r);
      setView(previewView(res.data, isOwner));
      setAdjust(false);
      setRequestId(newRequestId());
    } catch {
      setError("This needs a connection. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setReq(null);
    setView(null);
    setError(null);
    setAdjust(false);
    setLog([]);
    if (!target) return;
    if (target.kind === "edit") {
      const f = formFromEntry(target.entry, sareeNumber);
      setWas(f);
      setForm(f);
    } else {
      setWas(null);
      setForm(null);
      preview(batchFor(target));
    }
    const id = target.kind === "remove" ? null : target.entry.id;
    if (id) {
      getEntryChanges(id)
        .then((res: any) => setLog(res?.success && Array.isArray(res.data) ? res.data : []))
        .catch(() => setLog([]));
    }
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = todayIso();
  const problem = form && was ? formProblem(was, form, today, !!entry?.paid) : null;
  const change = useMemo(() => (form && was && entry ? changeFromForm(entry.id, was, form) : null), [form, was, entry]);

  const check = () => {
    if (!form || !change || problem) return;
    preview(changeRequest([change], form.reason));
  };

  const removeThis = async () => {
    if (!entry) return;
    const why = await askReason({ title: t("Remove this entry? Say why:") });
    if (!why) return;
    preview(batchRequest("REMOVE", [entry.id], why));
  };

  const confirm = async () => {
    if (!req || !view || !canConfirm(view, adjust)) return;
    setBusy(true);
    setError(null);
    try {
      const res: any = await saveEntryChanges(confirmRequest(req, view, requestId, adjust));
      if (!res?.success) {
        const msg = problemText(res);
        setError(msg);
        notifications({ title: t("Not saved"), message: t(msg), type: "danger" });
        // Nothing was written: the next try is a new request (BUSY: the first send may still be running).
        if (!keepRequestId(res?.code)) setRequestId(newRequestId());
        if (needsFreshPreview(res?.code)) {
          setView(null);
        } else if (needsPaidRecheck(res?.code)) {
          // Paid meanwhile: the fresh preview offers the owner "Adjust on next pay sheet".
          await preview(req, msg);
        } else {
          const next = refusalView(view, res?.data, isOwner);
          if (next) {
            setView(next);
            setAdjust(false);
          }
        }
        return;
      }
      const waiting = (res.data?.rows || []).some((r: any) => r.status === "PENDING_APPROVAL");
      notifications({
        title: t("Saved"),
        message: res.duplicate
          ? t("Already saved earlier; nothing was saved twice.")
          : waiting
          ? t("Sent to Approvals; it counts once the owner approves.")
          : t("Entry changed; pay worked out again."),
        type: "success",
      });
      onDone();
      onClose();
    } catch {
      // The answer was lost, not necessarily the save: keep the request id so a retry can't save twice.
      setError(NO_CONNECTION);
    } finally {
      setBusy(false);
    }
  };

  const title = !target
    ? ""
    : target.kind === "edit"
      ? t("Correct the entry for {{day}}", { day: fmtDay(target.entry.date) })
      : target.kind === "remove"
        ? target.entries.length === 1
          ? t("Remove 1 entry")
          : t("Remove {{n}} entries", { n: target.entries.length })
        : t("Restore the entry for {{day}}", { day: fmtDay(target.entry.date) });

  const set = (p: Partial<EntryForm>) => form && setForm({ ...form, ...p });
  const saree = t("saree");
  const line = (s: any) => snapshotLine(s, fmtDay, saree);

  return (
    <Modal isOpen={!!target} toggle={onClose} centered scrollable className="kh-change-dialog">
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {view ? (
          <>
            <ul className="kh-change-list" aria-label={t("What changes")}>
              {view.rows.map((r) => (
                <li key={r.entryId} className={`kh-change-row${r.status === "FIX" ? " is-fix" : ""}`}>
                  <div className="kh-change-row__line">
                    <span className={r.after ? "" : "is-gone"}>{line(r.before)}</span>
                    {r.action !== "REMOVE" && r.after ? (
                      <>
                        <span aria-hidden> → </span>
                        <span>{line(r.after)}</span>
                      </>
                    ) : null}
                  </div>
                  <div className="kh-change-row__meta">
                    <span className="kw-chip">{t(ACTION_LABEL[r.action] || r.action)}</span>
                    {r.pay ? (
                      <span>
                        {t("Pay")} {rupees(r.pay.before)} → {rupees(r.pay.after)}
                      </span>
                    ) : null}
                    {r.goesToApprovals ? <span className="kw-chip is-review">{t("goes to Approvals")}</span> : null}
                  </div>
                  {r.paid ? (
                    <p className="kh-change-row__paid">{t("Already paid on {{sheet}}: {{amount}}", { sheet: r.paid.label, amount: rupees(r.paid.amount) })}</p>
                  ) : null}
                  {r.problem ? (
                    <p className="kh-change-row__problem" role="alert">
                      {t(r.problem)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
            {view.summary ? <p className="kw-muted">{t(view.summary)}</p> : null}
            {view.needsAdjust && view.canAdjust ? (
              <label className="kh-change-adjust">
                <input type="checkbox" checked={adjust} onChange={(e) => setAdjust(e.target.checked)} />
                <span>
                  <strong>{t("Adjust on next pay sheet")}</strong>
                  <small>{t("What was paid comes off the next pay sheet as a marked correction line.")}</small>
                </span>
              </label>
            ) : null}
            {view.needsAdjust && !view.canAdjust ? <p className="kw-muted">{t("This entry is already paid. Only the owner can change it.")}</p> : null}
          </>
        ) : form ? (
          <>
            <div className="kh-change-grid">
              <div>
                <label className="kr-label" htmlFor="ke-date">
                  {t("Date")}
                </label>
                <input id="ke-date" type="date" className="kr-select" value={form.date} max={today} onChange={(e) => set({ date: e.target.value })} />
              </div>
              <div>
                <label className="kr-label" htmlFor="ke-saree">
                  {t("Move to another saree")}
                </label>
                <input
                  id="ke-saree"
                  className="kr-select"
                  inputMode="numeric"
                  value={form.sareeNumber}
                  placeholder={sareeNumber}
                  onChange={(e) => set({ sareeNumber: cleanSareeNumber(e.target.value) })}
                />
              </div>
            </div>
            <label className="kr-label mt-2" htmlFor="ke-weaver">
              {t("Weaver")}
            </label>
            <select id="ke-weaver" className="kr-select" value={form.weaverId} onChange={(e) => set({ weaverId: e.target.value })}>
              {roster.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} {p.name}
                </option>
              ))}
            </select>
            <label className="kr-label mt-2" htmlFor="ke-helper">
              {t("Helper")}
            </label>
            <select id="ke-helper" className="kr-select" value={form.helperId} onChange={(e) => set({ helperId: e.target.value })}>
              <option value="">{t("Alone (no helper)")}</option>
              {roster.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} {p.name}
                </option>
              ))}
            </select>
            <div className="kh-change-grid">
              <div>
                <label className="kr-label mt-2" htmlFor="ke-part">
                  {t("Part")}
                </label>
                <select id="ke-part" className="kr-select" value={form.workTypeId} onChange={(e) => set({ workTypeId: e.target.value })}>
                  {types.map((w) => (
                    <option key={w.id} value={w.id}>
                      {partName(w, lang, t)} ({w.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="kr-label mt-2" htmlFor="ke-inches">
                  {t("Inches")}
                </label>
                <input
                  id="ke-inches"
                  className="kr-select"
                  inputMode="decimal"
                  value={form.inches}
                  onChange={(e) => set({ inches: e.target.value.replace(/[^\d.]/g, "") })}
                />
              </div>
            </div>
            <label className="kr-label mt-2" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={form.ladkaSool} onChange={(e) => set({ ladkaSool: e.target.checked })} />
              {t("Woven with ladka sool")}
            </label>
            <label className="kr-label mt-2" htmlFor="ke-why">
              {change && change.action === "EDIT" && !entry?.paid ? t("Why (optional)") : t("Why")}
            </label>
            <input id="ke-why" className="kr-select" value={form.reason} maxLength={300} onChange={(e) => set({ reason: e.target.value })} />
            {entry?.paid ? <p className="kw-muted mt-2">{t("This entry is already paid. A change takes it off its pay sheet line.")}</p> : null}
            {problem && problem !== "Nothing changed yet" ? <p className="kw-muted mt-2">{t(problem)}</p> : null}
            <button type="button" className="btn btn-link kh-change-remove" disabled={busy} onClick={removeThis}>
              {t("Remove entry")}
            </button>
          </>
        ) : null}
        {error ? (
          <p className="kh-change-error" role="alert">
            {t(error)}
          </p>
        ) : null}
        {log.length ? (
          <details className="kh-change-log">
            <summary>{t("Earlier changes ({{n}})", { n: log.length })}</summary>
            <ul className="kh-changes">
              {log.map((c, i) => (
                <li key={i}>
                  <strong>{t(ACTION_LABEL[String(c.action)] || String(c.action || ""))}</strong>
                  {c.from || c.to ? `: ${line(c.from) || "—"} → ${line(c.to) || "—"}` : ""}
                  <small>
                    {c.byName || t("Someone")}
                    {c.at ? ` · ${new Date(c.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""}
                    {c.source === "MCP" ? ` · ${t("by the AI assistant")}` : ""}
                    {c.reason ? ` · “${c.reason}”` : ""}
                  </small>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </ModalBody>
      <ModalFooter>
        {view && form ? (
          <button type="button" className="kh-btn" disabled={busy} onClick={() => setView(null)}>
            {t("Back")}
          </button>
        ) : (
          <button type="button" className="kh-btn" onClick={onClose}>
            {t("Cancel")}
          </button>
        )}
        {view && view.blocked && req ? (
          // A refused row: nothing can be confirmed until the change is checked again.
          <button type="button" className="kh-btn is-primary" disabled={busy} onClick={() => preview(req)}>
            {busy ? t("Checking…") : t("Check again")}
          </button>
        ) : view ? (
          <button type="button" className="kh-btn is-primary" disabled={busy || !canConfirm(view, adjust)} onClick={confirm}>
            {busy ? t("Saving…") : t("Confirm")}
          </button>
        ) : form ? (
          <button type="button" className="kh-btn is-primary" disabled={busy || !!problem} onClick={check}>
            {busy ? t("Checking…") : t("Check")}
          </button>
        ) : target && target.kind !== "edit" ? (
          <button type="button" className="kh-btn is-primary" disabled={busy} onClick={() => preview(req || batchFor(target))}>
            {busy ? t("Checking…") : t("Check again")}
          </button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
};

export default EntryChangeDialog;
